import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandLessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HolidayVillage as HolidayIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DayData {
  dayOfWeek: string;
  objectives: string;
  activities: string;
  materials: string;
  homework: string;
  isHoliday: boolean;
  holidayType?: string;
  holidayReason?: string;
  isReviewDay: boolean;
  customTitle: string;
  notes: string;
}

interface WeekData {
  weekNumber: number;
  weekName: string;
  days: DayData[];
}

interface TabularDayPlannerProps {
  units: any[];
  onSave: (updatedUnits: any[]) => void;
  onClose: () => void;
  loading?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const HOLIDAY_TYPES = [
  { value: 'weekly_off', label: 'Weekly Off' },
  { value: 'government', label: 'Government Holiday' },
  { value: 'school', label: 'School Holiday' },
  { value: 'festival', label: 'Festival' },
  { value: 'exam', label: 'Exam/Assessment' },
  { value: 'emergency', label: 'Emergency Closure' },
  { value: 'other', label: 'Other' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function TabularDayPlanner({ units, onSave, onClose, loading = false }: TabularDayPlannerProps) {
  const { showSnackBar } = useSnackBar();
  const [expandedWeeks, setExpandedWeeks] = useState<Record<string, boolean>>({});
  const [tableData, setTableData] = useState<WeekData[]>([]);
  console.log("tableData", tableData);
  const [editing, setEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);

  // ── Initialize Data ────────────────────────────────────────────────────────

  useEffect(() => {
    setTableLoading(true);
    const data: WeekData[] = [];

    units.forEach((unit) => {
      unit.topics.forEach((topic: any) => {
        topic.weeks.forEach((week: any) => {
          const days = week.days || [];
          const weekDays: DayData[] = DAYS_OF_WEEK.map((dayOfWeek) => {
            const existingDay = days.find((d: any) => d.dayOfWeek === dayOfWeek);
            return {
              dayOfWeek,
              objectives: existingDay?.objectives?.join(', ') || '',
              activities: existingDay?.activities?.join(', ') || '',
              materials: existingDay?.materials?.join(', ') || '',
              homework: existingDay?.homework || '',
              isHoliday: existingDay?.isHoliday || false,
              holidayType: existingDay?.holidayType || '',
              holidayReason: existingDay?.holidayReason || '',
              isReviewDay: existingDay?.isReviewDay || false,
              customTitle: existingDay?.customTitle || '',
              notes: existingDay?.notes || '',
            };
          });

          data.push({
            weekNumber: week.weekNumber || data.length + 1,
            weekName: week.name || `Week ${data.length + 1}`,
            days: weekDays,
          });

          if (data.length === 1) {
            setExpandedWeeks({ ['week_0']: true });
          }
        });
      });
    });

    if (data.length === 0 && units?.length > 0) {
      setTableLoading(false);
      showSnackBar(
        'No weeks found. Please add weeks first in the Knowledge Map.',
        'info'
      );
    }


    setTableData(data);
    setTableLoading(false);
  }, [units]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleDayChange = (
    weekIndex: number,
    dayIndex: number,
    field: keyof DayData,
    value: any
  ) => {
    setEditing(true);
    const newData = [...tableData];
    newData[weekIndex].days[dayIndex] = {
      ...newData[weekIndex].days[dayIndex],
      [field]: value,
    };
    setTableData(newData);
  };

  const toggleWeek = (weekIndex: number) => {
    const key = `week_${weekIndex}`;
    setExpandedWeeks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const expandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    tableData.forEach((_, index) => {
      allExpanded[`week_${index}`] = true;
    });
    setExpandedWeeks(allExpanded);
  };

  const collapseAll = () => {
    setExpandedWeeks({});
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);

      const updatedUnits = JSON.parse(JSON.stringify(units));
      let weekCounter = 0;

      updatedUnits.forEach((unit: any) => {
        unit.topics.forEach((topic: any) => {
          topic.weeks.forEach((week: any) => {
            const weekData = tableData[weekCounter];
            if (weekData) {
              week.days = weekData.days.map((dayData) => ({
                id: week.days?.find((d: any) => d.dayOfWeek === dayData.dayOfWeek)?.id || `day_${Date.now()}_${Math.random()}`,
                dayOfWeek: dayData.dayOfWeek,
                objectives: dayData.objectives ? dayData.objectives.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
                activities: dayData.activities ? dayData.activities.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
                materials: dayData.materials ? dayData.materials.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
                homework: dayData.homework || '',
                isHoliday: dayData.isHoliday,
                holidayType: dayData.holidayType || undefined,
                holidayReason: dayData.holidayReason || '',
                isReviewDay: dayData.isReviewDay,
                notes: dayData.notes || '',
                customTitle: dayData.customTitle || '',
              }));
              weekCounter++;
            }
          });
        });
      });

      onSave(updatedUnits);
      setEditing(false);
      showSnackBar('Day plan saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving day plan:', error);
      showSnackBar('Failed to save day plan', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all changes?')) {
      const data: WeekData[] = [];
      units.forEach((unit) => {
        unit.topics.forEach((topic: any) => {
          topic.weeks.forEach((week: any) => {
            const days = week.days || [];
            const weekDays: DayData[] = DAYS_OF_WEEK.map((dayOfWeek) => {
              const existingDay = days.find((d: any) => d.dayOfWeek === dayOfWeek);
              return {
                dayOfWeek,
                objectives: existingDay?.objectives?.join(', ') || '',
                activities: existingDay?.activities?.join(', ') || '',
                materials: existingDay?.materials?.join(', ') || '',
                homework: existingDay?.homework || '',
                isHoliday: existingDay?.isHoliday || false,
                holidayType: existingDay?.holidayType || '',
                holidayReason: existingDay?.holidayReason || '',
                isReviewDay: existingDay?.isReviewDay || false,
                customTitle: existingDay?.customTitle || '',
                notes: existingDay?.notes || '',
              };
            });
            data.push({
              weekNumber: week.weekNumber || data.length + 1,
              weekName: week.name || `Week ${data.length + 1}`,
              days: weekDays,
            });
          });
        });
      });
      setTableData(data);
      setEditing(false);
      showSnackBar('Reset to original data', 'info');
    }
  };

  const applyToAllWeeks = (field: keyof DayData, value: any) => {
    const newData = [...tableData];
    newData.forEach((week) => {
      week.days.forEach((day) => {
        day[field] = value;
      });
    });
    setTableData(newData);
    setEditing(true);
  };

  const applyToDayAcrossWeeks = (dayIndex: number, field: keyof DayData, value: any) => {
    const newData = [...tableData];
    newData.forEach((week) => {
      if (week.days[dayIndex]) {
        week.days[dayIndex][field] = value;
      }
    });
    setTableData(newData);
    setEditing(true);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (loading || tableLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
              📋 Tabular Day Planner
            </Typography>
            <Chip
              label={`${tableData.length} Weeks`}
              size="medium"
              sx={{ fontSize: '0.75rem', bgcolor: '#E0E7FF', height: 28 }}
            />
            {editing && (
              <Chip
                label="Unsaved Changes"
                color="warning"
                size="medium"
                sx={{ fontSize: '0.65rem', height: 28 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Button
              size="medium"
              variant="outlined"
              onClick={expandAll}
              sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: 32 }}
            >
              Expand All
            </Button>
            <Button
              size="medium"
              variant="outlined"
              onClick={collapseAll}
              sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: 32 }}
            >
              Collapse All
            </Button>
            <Button
              size="medium"
              variant="outlined"
              color="error"
              onClick={handleReset}
              disabled={!editing}
              sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: 32 }}
            >
              Reset
            </Button>
            <Button
              size="medium"
              variant="contained"
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              onClick={handleSave}
              disabled={!editing || saveLoading}
              sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: 32 }}
            >
              {saveLoading ? 'Saving...' : 'Save All'}
            </Button>
            <Button
              size="medium"
              variant="outlined"
              onClick={onClose}
              sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: 32 }}
            >
              Close
            </Button>
          </Box>
        </Box>

        {/* Quick Apply Actions */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569' }}>
            Quick Apply:
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => applyToAllWeeks('isReviewDay', true)}
            sx={{ fontSize: '0.65rem', textTransform: 'none', minHeight: 24 }}
          >
            All Review Days
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => applyToAllWeeks('isReviewDay', false)}
            sx={{ fontSize: '0.65rem', textTransform: 'none', minHeight: 24 }}
          >
            Clear Review Days
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const confirm = window.confirm('Mark all Sundays as holidays?');
              if (confirm) {
                const newData = [...tableData];
                newData.forEach((week) => {
                  week.days.forEach((day, index) => {
                    if (day.dayOfWeek === 'Sunday') {
                      day.isHoliday = true;
                      day.holidayType = 'weekly_off';
                      day.holidayReason = 'Weekly Holiday';
                    }
                  });
                });
                setTableData(newData);
                setEditing(true);
              }
            }}
            sx={{ fontSize: '0.65rem', textTransform: 'none', minHeight: 24 }}
          >
            Set All Sundays as Holiday
          </Button>
        </Box>
      </Paper>

      {/* Table View */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 80, bgcolor: '#F8FAFC', py: 1.5 }}>
                Week
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#F8FAFC', py: 1.5 }}>
                Day
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 180, bgcolor: '#F8FAFC', py: 1.5 }}>
                Objectives
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 180, bgcolor: '#F8FAFC', py: 1.5 }}>
                Activities
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 180, bgcolor: '#F8FAFC', py: 1.5 }}>
                Materials
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 140, bgcolor: '#F8FAFC', py: 1.5 }}>
                Homework
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 120, bgcolor: '#F8FAFC', py: 1.5 }}>
                Custom Title
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 100, bgcolor: '#F8FAFC', py: 1.5, textAlign: 'center' }}>
                Holiday
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 80, bgcolor: '#F8FAFC', py: 1.5, textAlign: 'center' }}>
                Review
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((week, weekIndex) => (
              <React.Fragment key={weekIndex}>
                {/* Week Header Row */}
                <TableRow
                  sx={{
                    bgcolor: '#F1F5F9',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#E2E8F0' },
                  }}
                  onClick={() => toggleWeek(weekIndex)}
                >
                  <TableCell
                    colSpan={9}
                    sx={{
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        {expandedWeeks[`week_${weekIndex}`] ?
                          <ExpandLessIcon sx={{ fontSize: 20 }} /> :
                          <ExpandMoreIcon sx={{ fontSize: 20 }} />
                        }
                      </IconButton>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        Week {week.weekNumber}: {week.weekName}
                      </Typography>
                      <Chip
                        label={`${week.days.filter(d => d.isHoliday).length} holidays`}
                        size="small"
                        sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#FEE2E2', color: '#991B1B' }}
                      />
                      <Chip
                        label={`${week.days.filter(d => d.isReviewDay).length} review days`}
                        size="small"
                        sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#FEF3C7', color: '#92400E' }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Day Rows */}
                <TableRow>
                  <TableCell colSpan={9} sx={{ p: 0 }}>
                    <Collapse in={expandedWeeks[`week_${weekIndex}`]}>
                      <Box>
                        {week.days.map((day, dayIndex) => (
                          <TableRow
                            key={dayIndex}
                            sx={{
                              '&:nth-of-type(even)': { bgcolor: '#FAFBFC' },
                              bgcolor: day.isHoliday ? '#FEF2F2' : 'transparent',
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.7rem', pl: 5, py: 0.5 }}>
                              <Chip
                                label={day.dayOfWeek.substring(0, 3)}
                                size="small"
                                sx={{
                                  fontSize: '0.55rem',
                                  height: 20,
                                  bgcolor: day.isHoliday ? '#EF4444' :
                                    (['Saturday', 'Sunday'].includes(day.dayOfWeek) ? '#F59E0B' : '#3B82F6'),
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                              {day.isReviewDay && !day.isHoliday && (
                                <Chip
                                  label="📝"
                                  size="small"
                                  sx={{ fontSize: '0.4rem', height: 16, bgcolor: '#FEF3C7', ml: 0.5 }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={day.objectives}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'objectives', e.target.value)}
                                placeholder="Comma separated"
                                disabled={day.isHoliday}
                                sx={{
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 },
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={day.activities}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'activities', e.target.value)}
                                placeholder="Comma separated"
                                disabled={day.isHoliday}
                                sx={{
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 },
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={day.materials}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'materials', e.target.value)}
                                placeholder="Comma separated"
                                disabled={day.isHoliday}
                                sx={{
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 },
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={day.homework}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'homework', e.target.value)}
                                disabled={day.isHoliday}
                                sx={{
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 },
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={day.customTitle}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'customTitle', e.target.value)}
                                disabled={day.isHoliday}
                                sx={{
                                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 },
                                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Checkbox
                                  size="medium"
                                  checked={day.isHoliday}
                                  onChange={(e) => {
                                    const newData = [...tableData];
                                    newData[weekIndex].days[dayIndex].isHoliday = e.target.checked;
                                    if (!e.target.checked) {
                                      newData[weekIndex].days[dayIndex].holidayType = '';
                                      newData[weekIndex].days[dayIndex].holidayReason = '';
                                    }
                                    setTableData(newData);
                                    setEditing(true);
                                  }}
                                  sx={{ p: 0.5 }}
                                />
                                {day.isHoliday && (
                                  <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                      value={day.holidayType || ''}
                                      onChange={(e) => handleDayChange(weekIndex, dayIndex, 'holidayType', e.target.value)}
                                      displayEmpty
                                      sx={{ fontSize: '0.7rem', height: 28 }}
                                    >
                                      <MenuItem value="" sx={{ fontSize: '0.7rem' }}>Type</MenuItem>
                                      {HOLIDAY_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.7rem' }}>
                                          {type.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ p: 0.5, textAlign: 'center' }}>
                              <Checkbox
                                size="medium"
                                checked={day.isReviewDay}
                                onChange={(e) => handleDayChange(weekIndex, dayIndex, 'isReviewDay', e.target.checked)}
                                disabled={day.isHoliday}
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
          💡 Tip: Use comma-separated values for multiple objectives, activities, and materials.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="medium"
            variant="outlined"
            onClick={onClose}
            sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
          >
            Close
          </Button>
          <Button
            size="medium"
            variant="contained"
            startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
            onClick={handleSave}
            disabled={!editing || saveLoading}
            sx={{ fontSize: '0.75rem', textTransform: 'none', minHeight: 36 }}
          >
            {saveLoading ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}